import { X, Plus, MinusCircle, ChevronDown } from "lucide-react";
import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { BASE_URL, API_ENDPOINTS } from "../../config/api.config";

const createTaskId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `task_${Math.random().toString(36).slice(2, 10)}`;
};

const AddTask = ({
  onClose,
  requestId,
  recipientId,
  assigneeName,
  assigneeNonprofitId,
  onSuccess,
  categories = [],
}) => {
  const [tasks, setTasks] = useState([]);
  const [taskDraft, setTaskDraft] = useState({
    taskName: "",
    specialRequirements: "",
    countNeeded: "",
    quantityType: "Unit",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [localCategories, setLocalCategories] = useState(categories);

  // Fetch user services (categories) on component mount if not provided
  useEffect(() => {
    if (categories && categories.length > 0) {
      setLocalCategories(categories);
      return;
    }

    const fetchUserServices = async () => {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        return;
      }

      setIsLoadingCategories(true);
      try {
        const response = await fetch(
          `${BASE_URL}${API_ENDPOINTS.GET_USER_SERVICES}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          const result = await response.json();
          const services = result?.data || result;
          setLocalCategories(Array.isArray(services) ? services : []);
        } else {
          console.error("Failed to fetch user services");
        }
      } catch (error) {
        console.error("Error fetching user services:", error);
      } finally {
        setIsLoadingCategories(false);
      }
    };

    fetchUserServices();
  }, [categories]);

  const handleTaskDraftChange = (field) => (event) => {
    setTaskDraft((prev) => ({
      ...prev,
      [field]:
        field === "countNeeded"
          ? event.target.value.replace(/\D/g, "")
          : event.target.value,
    }));
  };

  const handleAddTask = () => {
    if (!taskDraft.taskName.trim() || !taskDraft.countNeeded) {
      toast.error("Please fill in type and quantity");
      return;
    }

    const finalTaskName = taskDraft.specialRequirements?.trim()
      ? `${taskDraft.taskName.trim()} - ${taskDraft.specialRequirements.trim()}`
      : taskDraft.taskName.trim();

    setTasks((prev) => [
      ...prev,
      {
        id: createTaskId(),
        taskName: finalTaskName,
        countNeeded: Number(taskDraft.countNeeded),
        quantityType: taskDraft.quantityType || "Unit",
      },
    ]);

    setTaskDraft({
      taskName: "",
      specialRequirements: "",
      countNeeded: "",
      quantityType: "Unit",
    });
  };

  const handleRemoveTask = (taskId) => {
    setTasks((prev) => prev.filter((task) => task.id !== taskId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (tasks.length === 0) {
      toast.error("Please add at least one resource");
      return;
    }

    const token = localStorage.getItem("accessToken");
    if (!token) {
      toast.error("Session expired. Please sign in again.");
      return;
    }

    setIsSaving(true);
    try {
      // Add all tasks sequentially
      for (const task of tasks) {
        const response = await fetch(
          `${BASE_URL}${API_ENDPOINTS.NONPROFIT_ADD_TASK}/${requestId}/recipients/${recipientId}/tasks`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              taskName: task.taskName,
              countNeeded: task.countNeeded,
              assigneeNonprofitId: assigneeNonprofitId,
            }),
          }
        );

        const result = await response.json().catch(() => null);
        const statusCode = result?.statusCode ?? response.status;

        if (!response.ok || statusCode >= 400) {
          throw new Error(result?.message || "Failed to add task");
        }
      }

      toast.success("Resources added successfully");
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error("Add task error:", error);
      toast.error(error?.message || "Failed to add resources");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-50 transition-opacity"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="rounded-lg overflow-hidden shadow-2xl w-full max-w-2xl max-h-[90vh] bg-white flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-center p-4 mb-4 text-black relative border-b border-border">
            <div>
              <h2 className="text-lg lg:text-xl font-popinsSemiBold">
                Send Collaboration Request
              </h2>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 absolute right-3 top-3 bg-white/20 hover:bg-white/30 rounded-full text-lg flex items-center justify-center transition-all hover:rotate-90"
            >
              <X className="w-6 h-6 text-foreground" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 pb-6">
            <div className="w-full mb-4">
              <label className="font-popinsMedium text-sm">Assigned to</label>
              <input
                type="text"
                value={assigneeName || ""}
                readOnly
                disabled
                className="w-full mt-2 font-popinsMedium rounded-md border-2 border-border bg-gray-100 px-4 py-2 text-black/60 outline-none cursor-not-allowed"
              />
            </div>

            {/* Resources Section */}
            <div className="mt-6">
              <h3 className="text-lg font-popinsSemiBold mb-4">Resources</h3>

              {/* Add Resource Form */}
              <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-4">
                <div className="w-full lg:flex-1">
                  <div className="relative">
                    <select
                      className={`w-full font-popinsMedium rounded-lg border-2 border-gray-300 bg-white px-4 py-2.5 pr-10 outline-none transition-all duration-200 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_0_3px_rgba(63,196,227,0.1)] cursor-pointer appearance-none ${taskDraft.taskName ? "text-gray-900" : "text-gray-400"
                        }`}
                      value={taskDraft.taskName}
                      onChange={handleTaskDraftChange("taskName")}
                      disabled={isSaving || isLoadingCategories}
                    >
                      <option value="" disabled className="text-gray-400">
                        Select Resource Type
                      </option>
                      {localCategories && localCategories.map((category) => (
                        <option key={category.id} value={category.value} className="text-gray-900">
                          {category.name}
                        </option>
                      ))}
                      <option value="OTHER" className="text-gray-900">
                        Other
                      </option>
                    </select>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                      <ChevronDown size={18} />
                    </div>
                  </div>
                </div>
                <div className="w-full lg:w-auto lg:min-w-[200px] flex flex-col gap-2">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Qty."
                      className="w-20 font-popinsMedium rounded-lg border-2 border-gray-300 bg-white px-3 py-2.5 text-gray-900 placeholder-gray-400 outline-none transition-all duration-200 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_0_3px_rgba(63,196,227,0.1)]"
                      value={taskDraft.countNeeded}
                      onChange={handleTaskDraftChange("countNeeded")}
                      inputMode="numeric"
                      disabled={isSaving}
                    />
                    <div className="relative w-28">
                      <select
                        className="w-full font-popinsMedium rounded-lg border-2 border-gray-300 bg-white px-3 py-2.5 pr-8 outline-none transition-all duration-200 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_0_3px_rgba(63,196,227,0.1)] cursor-pointer appearance-none text-gray-900"
                        value={taskDraft.quantityType}
                        onChange={handleTaskDraftChange("quantityType")}
                        disabled={isSaving}
                      >
                        <option value="Unit">Unit(s)</option>
                        <option value="Person">Person(s)</option>
                      </select>
                      <div className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                        <ChevronDown size={18} />
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleAddTask}
                  className="relative overflow-hidden py-2.5 px-6 bg-themeColor text-white font-popinsSemiBold text-sm rounded-lg shadow-md transform transition-all duration-200 ease-in-out hover:bg-[#35b3d1] hover:shadow-lg active:scale-95 focus:outline-none focus:ring-2 focus:ring-[#3FC4E3] focus:ring-offset-2 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:shadow-md whitespace-nowrap"
                  disabled={
                    isSaving || !taskDraft.countNeeded || !taskDraft.taskName
                  }
                >
                  <Plus size={16} strokeWidth={2.5} className="text-white" />
                  <span>Add</span>
                </button>
              </div>
              <div className="mt-5">
                <label className="font-popinsMedium text-sm text-gray-700 mb-1.5 block">
                  Special Requirements
                </label>
                <textarea
                  rows="4"
                  className="w-full font-popinsMedium rounded-lg border-2 border-gray-300 bg-white px-4 py-3 text-gray-900 placeholder-gray-400 outline-none transition-all duration-200 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_0_3px_rgba(63,196,227,0.1)] resize-none"
                  value={taskDraft.specialRequirements}
                  onChange={handleTaskDraftChange("specialRequirements")}
                  placeholder="Add more details to your resource requirements"
                  disabled={isSaving}
                ></textarea>
              </div>

              {/* Resources List */}
              {tasks.length > 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                  <div className="flex border-b border-gray-200 bg-gray-50 px-5 py-3.5">
                    <div className="w-full">
                      <p className="font-popinsSemiBold text-sm text-gray-700">
                        Resource Details
                      </p>
                    </div>
                    <div className="w-[20%]">
                      <p className="font-popinsSemiBold text-sm text-gray-700">
                        Quantity
                      </p>
                    </div>
                    <div className="w-[15%] flex justify-end items-center pr-4">
                      <span className="sr-only">Actions</span>
                    </div>
                  </div>
                  {tasks.map((task, index) => (
                    <div
                      key={task.id}
                      className={`flex border-b last:border-b-0 border-gray-100 text-gray-700 py-4 px-5 items-center hover:bg-gray-50 transition-colors ${
                        index % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                      }`}
                    >
                      <div className="w-full">
                        <p className="font-popinsMedium text-sm text-gray-900">
                          {task.taskName}
                        </p>
                      </div>
                      <div className="w-[20%]">
                        <p className="font-popinsMedium text-sm text-gray-900">
                          {task.countNeeded}{" "}
                          {task.quantityType === "Person"
                            ? "Person(s)"
                            : "Unit(s)"}
                        </p>
                      </div>
                      <div className="w-[15%] flex justify-end pr-2">
                        <button
                          type="button"
                          onClick={() => handleRemoveTask(task.id)}
                          className="text-red-500 hover:text-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed p-1 rounded hover:bg-red-50 flex-shrink-0"
                          disabled={isSaving}
                          title="Remove resource"
                        >
                          <MinusCircle size={18} aria-hidden />
                          <span className="sr-only">Remove resource</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {tasks.length === 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                  <div className="py-8 text-center text-gray-400 font-popinsRegular text-sm">
                    No resources added yet. Resources are optional.
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-x-3 p-6 border-t border-border">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="relative overflow-hidden py-2.5 px-8 bg-[#F0F5F2] text-black font-popinsSemiBold text-sm rounded-md shadow-lg transform transition duration-300 ease-in-out hover:bg-gray-200 hover:scale-105 active:scale-95 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSaving || tasks.length === 0}
              className="relative overflow-hidden py-2.5 px-8 bg-themeColor text-white font-popinsSemiBold text-sm rounded-md shadow-lg transform transition duration-300 ease-in-out hover:bg-[#3FC4E3] hover:scale-105 hover:shadow-[0_0_20px_rgba(63,196,227,0.6)] active:scale-95 active:shadow-inner focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSaving ? "Saving..." : "Save"}
              <span className="absolute inset-0 rounded-full bg-white opacity-0 active:opacity-10 transition-opacity duration-300"></span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddTask;
